import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Trash2, ArrowLeft, Send, ShieldOff } from 'lucide-react';
import { storeApi, getCart, minOrderQty, cartSetQty, cartRemove, cartClear } from './storeApi';
import StoreLayout from './StoreLayout';
import QtyControl from './QtyControl';
import DuplicatePendingModal from '../../components/DuplicatePendingModal';

export default function StoreCart() {
  const [cart, setCart] = useState(getCart());
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Block-modal: shown when admin has paused order acceptance.
  const [blockModal, setBlockModal] = useState(null);
  // Duplicate-pending modal — shown when one or more cart lines already
  // have open pending procurement on previous orders. Warning-only.
  const [dupModal, setDupModal] = useState(null); // { matches, periodDays }
  const nav = useNavigate();

  // Keep our local copy in sync with the cart store (server-backed).
  useEffect(() => {
    const onChange = (e) => setCart(e?.detail?.cart || getCart());
    window.addEventListener('storeCartChanged', onChange);
    return () => window.removeEventListener('storeCartChanged', onChange);
  }, []);

  const lines = Object.values(cart);
  const totalMrp = lines.reduce((s, l) => s + Number(l.product.mrp || 0) * Number(l.qty || 0), 0);
  const totalQty = lines.reduce((s, l) => s + Number(l.qty || 0), 0);

  // setQty — used by QtyControl for direct input + quick-add chips.
  // Receives the absolute target qty (already clamped/validated upstream).
  function setQty(pid, target) {
    const line = cart[pid];
    if (!line) return;
    const minQty = minOrderQty(line.product);
    const desired = Number.isFinite(target) ? Math.floor(target) : 0;
    const final = (desired <= 0 || desired < minQty) ? 0 : desired;
    cartSetQty(line.product, final).catch((e) => toast.error(e?.message || 'Failed to update cart'));
  }

  function removeLine(pid) {
    cartRemove(pid).catch((e) => toast.error(e?.message || 'Failed to remove'));
  }

  function placeOrder() {
    if (lines.length === 0) return;
    setSubmitting(true);
    // Pre-check the order-acceptance gate BEFORE attempting the order so the
    // user gets a clean modal instead of a generic toast. Backend re-checks
    // the same gate so this is purely a UX optimisation.
    storeApi.get('/store/order-control/status')
      .then((statusResp) => {
        if (statusResp.data && statusResp.data.accepting_orders === false) {
          setBlockModal({
            title: statusResp.data.reason === 'scheduled'
              ? 'Order Placement — Scheduled Pause'
              : 'Order Temporarily Blocked',
            message: statusResp.data.message || 'Order placement is currently disabled by the administrator. Please contact support or try again later. Thank you.',
            scheduledUntil: statusResp.data.scheduled_until,
          });
          setSubmitting(false);
          return null;
        }
        // Duplicate-pending check — warning only; user can always continue.
        return storeApi.post('/store/orders/check-duplicate-pending', {
          franchise_id: '__from_token__',  // ignored server-side
          items: lines.map((l) => ({ product_id: l.product.id, quantity: Number(l.qty) })),
        }).then((dupResp) => {
          const matches = dupResp.data?.matches || [];
          if (matches.length > 0) {
            // Stop here — the modal's YES button calls submitOrder() directly.
            setDupModal({ matches, periodDays: dupResp.data?.period_days });
            setSubmitting(false);
            return null;
          }
          return submitOrder();
        });
      })
      .catch(handlePlaceOrderError);
  }

  // The actual POST /store/orders call — split out so we can call it
  // from the duplicate-pending modal's YES button too.
  function submitOrder() {
    setSubmitting(true);
    const payload = {
      items: lines.map((l) => ({ product_id: l.product.id, quantity: Number(l.qty) })),
      notes: notes || null,
    };
    return storeApi.post('/store/orders', payload)
      .then((r) => {
        toast.success(`Order ${r.data.order_number} placed!`);
        // Server-side cart clear AFTER successful order creation — per spec
        // we never clear before success (so users can retry on failure).
        cartClear().catch(() => { /* non-blocking — cache already cleared */ });
        setCart({});
        setDupModal(null);
        nav('/store/orders');
      })
      .catch(handlePlaceOrderError)
      .finally(() => setSubmitting(false));
  }

  function handlePlaceOrderError(e) {
    // Backend may also return 423 LOCKED if the gate flips between our
    // pre-check and the actual create — handle that gracefully too.
    if (e?.response?.status === 423) {
      setBlockModal({
        title: 'Order Temporarily Blocked',
        message: e.response.data?.detail || 'Order placement is currently disabled.',
      });
    } else {
      toast.error(e?.response?.data?.detail || 'Failed to place order');
    }
    setSubmitting(false);
  }

  return (
    <StoreLayout>
      <div className="flex items-center justify-between mb-6">
        <h1 className="store-headline text-3xl">Your Cart</h1>
        <button onClick={() => nav('/store/catalogue')} className="text-sm text-orange-700 flex items-center gap-1 hover:underline" data-testid="back-to-catalogue">
          <ArrowLeft className="w-4 h-4" /> Continue shopping
        </button>
      </div>

      {lines.length === 0 ? (
        <div className="store-card p-12 text-center text-slate-400">
          Your cart is empty. <button onClick={() => nav('/store/catalogue')} className="text-orange-700 underline ml-1">Browse catalogue</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3" data-testid="cart-lines">
            {lines.map((l) => (
              <div key={l.product.id} className="store-card p-4 flex items-center gap-3" data-testid={`cart-line-${l.product.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-orange-950 truncate">{l.product.name}</p>
                  <p className="text-xs text-slate-400">
                    MRP <span className="text-slate-700 font-medium">₹{Number(l.product.mrp).toFixed(2)}</span>
                    <span className="ml-2 text-slate-400">/ {l.product.unit || 'PCS'}</span>
                  </p>
                </div>
                <QtyControl
                  value={l.qty}
                  onChange={(newQty) => setQty(l.product.id, newQty)}
                  min={minOrderQty(l.product)}
                  quickAdds={[10, 25, 50, 100]}
                  testidPrefix={`cart-qty-${l.product.id}`}
                />
                <div className="text-right min-w-[80px]">
                  <p className="font-bold text-slate-800">₹{(Number(l.product.mrp) * Number(l.qty)).toFixed(2)}</p>
                  <button onClick={() => removeLine(l.product.id)} className="text-xs text-red-500 hover:underline"
                          data-testid={`cart-remove-${l.product.id}`}>
                    <Trash2 className="w-3 h-3 inline" /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <aside className="store-card p-5 h-fit sticky top-4">
            <h3 className="store-headline text-xl mb-4">Order summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>Total items</span><span>{totalQty}</span></div>
              <div className="flex justify-between"><span>Total lines</span><span>{lines.length}</span></div>
              <div className="flex justify-between font-bold text-lg pt-3 border-t mt-3"><span>MRP total</span><span className="text-slate-800">₹{totalMrp.toFixed(2)}</span></div>
              <p className="text-[10px] text-slate-400 leading-tight">Final billing price will be confirmed on the invoice.</p>
            </div>
            <label className="block text-xs uppercase tracking-wide text-slate-500 mt-5 mb-1">Notes (optional)</label>
            <textarea className="store-input min-h-[64px]" value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Anything we should know about this order?" data-testid="cart-notes" />
            <button onClick={placeOrder} disabled={submitting || lines.length === 0}
                    className="store-btn-citrus w-full mt-5 flex items-center justify-center gap-2"
                    data-testid="place-order-btn">
              <Send className="w-4 h-4" />
              {submitting ? 'Placing…' : 'Place Order'}
            </button>
            <p className="text-[10px] text-slate-400 mt-3 text-center">
              Orders flow to the Pick List automatically once your items are ready.
            </p>
          </aside>
        </div>
      )}

      {/* Order-blocked modal — surfaced when admin has paused acceptance. */}
      {blockModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="order-blocked-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 border-2 border-red-200">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2.5 rounded-full bg-red-100">
                <ShieldOff className="text-red-700 w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-red-900 text-xl">{blockModal.title}</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  We can&apos;t accept your order right now.
                </p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-900 whitespace-pre-line">{blockModal.message}</p>
              {blockModal.scheduledUntil && (
                <p className="text-xs text-red-700 mt-2">
                  Resumes automatically at {new Date(blockModal.scheduledUntil).toLocaleString()}
                </p>
              )}
            </div>
            <p className="text-sm text-slate-600 mb-5 text-center">
              Your cart is saved. Please try again later.<br/>
              <span className="text-xs text-slate-400">Thank You.</span>
            </p>
            <button
              onClick={() => setBlockModal(null)}
              className="store-btn-citrus w-full"
              data-testid="order-blocked-close"
            >
              OK, Got It
            </button>
          </div>
        </div>
      )}

      <DuplicatePendingModal
        open={!!dupModal}
        matches={dupModal?.matches || []}
        periodDays={dupModal?.periodDays}
        onCancel={() => setDupModal(null)}
        onConfirm={submitOrder}
        testIdPrefix="store-dup"
      />
    </StoreLayout>
  );
}
